import type { CreateBankAccountInput } from '@financiar/shared';
import { isValidIban } from '@financiar/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Badge, Button, Form, Input, List, Popconfirm, Space, Typography } from 'antd';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { applyServerErrors, normalizeIban } from '../../lib/forms';
import type { CompanyBankAccount } from '../../api/types';
import { createBankAccount, deleteBankAccount, updateBankAccount } from './api';

interface BankAccountFormValues {
  bankName: string;
  iban: string;
  bic?: string;
  currency: string;
}

const DEFAULT_VALUES: BankAccountFormValues = { bankName: '', iban: '', bic: '', currency: 'RON' };

/**
 * "Conturi bancare" tab (edit mode only) — same "read off the parent's
 * FirmDetail fetch, invalidate to refresh" pattern as FirmaVatPeriodsTab.
 * One inline `Form` doubles as both the "add" and "edit" form (`editingId`
 * decides which mutation `handleFinish` calls); "Setează implicit" is its
 * own one-click action, not part of the form.
 */
export function FirmaBankAccountsTab({ firmaId, accounts }: { firmaId: string; accounts: CompanyBankAccount[] }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<BankAccountFormValues>();
  const [editingId, setEditingId] = useState<string | null>(null);

  // Invalidates the whole `['firms', ...]` branch (not just this firm's own
  // detail query) — same reasoning as FirmaVatPeriodsTab: bank account
  // changes should also be visible to anything else reading firm data
  // (FirmeListPage's table, FirmaCurentaContext's header selector list).
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['firms'] });

  const createMutation = useMutation({
    mutationFn: (values: BankAccountFormValues) =>
      createBankAccount(firmaId, {
        bankName: values.bankName.trim(),
        iban: normalizeIban(values.iban),
        bic: values.bic?.trim() || undefined,
        currency: values.currency.trim().toUpperCase(),
      } as CreateBankAccountInput),
    onSuccess: () => {
      message.success('Contul bancar a fost adăugat.');
      form.resetFields();
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Contul bancar nu a putut fi adăugat.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: BankAccountFormValues }) =>
      updateBankAccount(id, {
        bankName: values.bankName.trim(),
        iban: normalizeIban(values.iban),
        bic: values.bic?.trim() || undefined,
        currency: values.currency.trim().toUpperCase(),
      }),
    onSuccess: () => {
      message.success('Contul bancar a fost actualizat.');
      form.resetFields();
      setEditingId(null);
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Contul bancar nu a putut fi actualizat.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBankAccount(id),
    onSuccess: () => {
      message.success('Contul bancar a fost șters.');
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Contul bancar nu a putut fi șters.');
    },
  });

  const defaultMutation = useMutation({
    mutationFn: (id: string) => updateBankAccount(id, { isDefault: true }),
    onSuccess: () => {
      message.success('Contul a fost setat ca implicit.');
      invalidate();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Contul implicit nu a putut fi schimbat.');
    },
  });

  function startEdit(account: CompanyBankAccount) {
    setEditingId(account.id);
    form.setFieldsValue({
      bankName: account.bankName,
      iban: account.iban,
      bic: account.bic ?? undefined,
      currency: account.currency,
    });
  }
  function cancelEdit() {
    setEditingId(null);
    form.resetFields();
  }
  function handleFinish(values: BankAccountFormValues) {
    if (editingId) updateMutation.mutate({ id: editingId, values });
    else createMutation.mutate(values);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <Form<BankAccountFormValues>
        form={form}
        layout="inline"
        initialValues={DEFAULT_VALUES}
        onFinish={handleFinish}
        style={{ marginBottom: 16, rowGap: 8 }}
      >
        <Form.Item name="bankName" rules={[{ required: true, message: 'Banca este obligatorie.' }]}>
          <Input placeholder="Bancă" />
        </Form.Item>
        <Form.Item
          name="iban"
          rules={[
            { required: true, message: 'IBAN este obligatoriu.' },
            {
              validator: (_, value: string) =>
                isValidIban(normalizeIban(value)) ? Promise.resolve() : Promise.reject(new Error('IBAN invalid.')),
            },
          ]}
        >
          <Input placeholder="IBAN" style={{ width: 260 }} />
        </Form.Item>
        <Form.Item name="bic">
          <Input placeholder="BIC/SWIFT (opțional)" style={{ width: 170 }} />
        </Form.Item>
        <Form.Item name="currency" rules={[{ required: true, len: 3, message: 'Moneda are 3 caractere.' }]}>
          <Input placeholder="RON" style={{ width: 80 }} maxLength={3} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isSaving}>
              {editingId ? 'Salvează' : 'Adaugă'}
            </Button>
            {editingId && <Button onClick={cancelEdit}>Anulează</Button>}
          </Space>
        </Form.Item>
      </Form>

      {accounts.length === 0 ? (
        <Typography.Text type="secondary">Niciun cont bancar înregistrat.</Typography.Text>
      ) : (
        <List
          dataSource={accounts}
          renderItem={(account) => (
            <List.Item
              actions={[
                !account.isDefault && (
                  <Button
                    key="default"
                    size="small"
                    loading={defaultMutation.isPending && defaultMutation.variables === account.id}
                    onClick={() => defaultMutation.mutate(account.id)}
                  >
                    Setează implicit
                  </Button>
                ),
                <Button key="edit" size="small" onClick={() => startEdit(account)}>
                  Editează
                </Button>,
                <Popconfirm key="del" title="Ștergi contul?" onConfirm={() => deleteMutation.mutate(account.id)}>
                  <Button size="small" danger loading={deleteMutation.isPending && deleteMutation.variables === account.id}>
                    Șterge
                  </Button>
                </Popconfirm>,
              ]}
            >
              {/* Meta stacks the name over the IBAN so a long IBAN + the
                  currency never get squeezed into a sliver next to the action
                  buttons (which used to wrap "RON" vertically as R/O/N). */}
              <List.Item.Meta
                title={
                  <Space size={8}>
                    {account.isDefault && <Badge status="success" text="Implicit" />}
                    <Typography.Text strong>{account.bankName}</Typography.Text>
                  </Space>
                }
                description={
                  <Space size={12} wrap>
                    <Typography.Text className="num" style={{ whiteSpace: 'nowrap' }}>
                      {account.iban}
                    </Typography.Text>
                    {account.bic ? (
                      <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                        {account.bic}
                      </Typography.Text>
                    ) : null}
                    <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                      {account.currency}
                    </Typography.Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
